import * as Sentry from "@sentry/nextjs"
import { Badge, Label, Radio } from "flowbite-react"
import { Formik, FormikHelpers, FormikProps, FormikProvider, useFormik } from "formik"
import { groupBy, uniqBy } from "lodash"
import React, { Dispatch, forwardRef, useEffect, useMemo, useRef, useState } from "react"
import useSWR from "swr"
import * as Yup from "yup"

import DangerouslyHTMLTextPriview from "@components/elements/DangerouslyHTMLTextPriview"
import { useDebounce } from "@hooks/useDebounce"
import UserIcon from "@icons/User"
import { addonDetailsType } from "@modules/tenants/OrdersPage/Components/AddOrderModal/ParticipationDetailsForm/ParticipationDetailsForm"
import * as ProductPricingServices from "@mono/essentials/services/ProductPricingServices"
import { ProductPricingType } from "@mono/essentials/types/ProductPricingTypes"
import { ProductAddonType } from "@mono/essentials/types/ProductTypes"
import { IDomain } from "@mono/essentials/types/UserTypes"

import { ParticipationDetailsFormAttributes } from "../ParticipantDetailsForm"

interface CustomProps {
  addOnList: any
  isIntegratedPage: boolean
  handleNextStep?: (arg0: any) => void
  participantsDetails: ParticipationDetailsFormAttributes
  domainDetails?: IDomain
  isLoader?: boolean
  setOrderSummery?: Dispatch<React.SetStateAction<any | undefined>>
  formInitialValues?: any
  formikRef: React.RefObject<FormikProps<any>>
  productInfo?: any
}

const AdditionalProductsList = forwardRef(
  ({
    addOnList,
    isIntegratedPage,
    handleNextStep,
    participantsDetails,
    setOrderSummery,
    formInitialValues,
    formikRef,
    productInfo,
  }: CustomProps) => {
    const [productAddonsPriceDataPayload, setProductAddonsPriceDataPayload] = useState<string[]>()
    const [debounceParams, setDebounceParams] = useState({})
    const [formSubmitting, setFormSubmitting] = useState(false)

    const debounced = useDebounce(debounceParams, 500)
    const [addonPrices, setAddOnPrices] = useState(formInitialValues?.addonPricing ?? {})

    const { data: productAddonsPriceData, isLoading: isProductAddonsPriceDataLoading } = useSWR(
      productAddonsPriceDataPayload?.length ? `productAddonsPriceData` : null,
      async () => {
        const allAddonsPriceDataResponse: Record<string, ProductPricingType[]> = {}
        await Promise.all(
          productAddonsPriceDataPayload.map((addonId) =>
            ProductPricingServices.getProductPricingByProdId(
              addonId,
              {},
              isIntegratedPage,
              ""
            ).then((resp) => {
              if (addonId && resp?.data?.data) allAddonsPriceDataResponse[addonId] = resp.data.data
            })
          )
        )
        if (Object.keys(allAddonsPriceDataResponse).length) {
          return allAddonsPriceDataResponse
        }
      },
      {
        revalidateOnFocus: false,
      }
    )

    const validationSchema = () => Yup.object().shape({})

    const onSubmit = async (
      values: any,
      { setFieldError, resetForm, setFieldValue }: FormikHelpers<ParticipationDetailsFormAttributes>
    ) => {
      try {
        setFormSubmitting(true)
        let isFormValidated = true
        // Check for mandatory add-ons
        let clonedAddonDetails = values.addonDetails.slice()
        ;(addOnList as unknown as ProductAddonType[]).forEach((addon) => {
          if (addon?.merchandise?.requirements === "required") {
            const enteredAddonDetailsIndex = clonedAddonDetails.findIndex(
              (ao) => ao.addonId === addon.id
            )
            if (enteredAddonDetailsIndex > -1) {
              if (clonedAddonDetails[enteredAddonDetailsIndex].requirement === "required") {
                if (clonedAddonDetails[enteredAddonDetailsIndex].availability === "customer") {
                  let requestedAddonsCount = 0
                  clonedAddonDetails[enteredAddonDetailsIndex].units.forEach(
                    (unit) => (requestedAddonsCount += unit.unitCount)
                  )
                  if (requestedAddonsCount === 0) {
                    isFormValidated = false
                    clonedAddonDetails[enteredAddonDetailsIndex].error =
                      "Add-on is mandatory based on your selection!"
                  } else {
                    clonedAddonDetails[enteredAddonDetailsIndex].error = ""
                  }
                } else {
                  // In edit add-ons flow, we need to uniqBy "participantId"
                  // In create add-ons flow, we need to uniqBy "participantGroupName"
                  const numberOfEditedAddons = clonedAddonDetails[
                    enteredAddonDetailsIndex
                  ].units?.filter((unit) => Boolean(unit.participantId))?.length
                  const uniqAddonUnitsWithCount = uniqBy(
                    clonedAddonDetails[enteredAddonDetailsIndex].units.filter(
                      (unit) => unit.unitCount > 0
                    ),
                    numberOfEditedAddons ===
                      clonedAddonDetails[enteredAddonDetailsIndex]?.units?.length
                      ? "participantId"
                      : "participantGroupName"
                  )
                  let totalParticipantsCount = 0
                  ;(productInfo?.units || []).forEach(
                    (participantGroup) => (totalParticipantsCount += participantGroup.unitCount)
                  )

                  if (uniqAddonUnitsWithCount.length === totalParticipantsCount) {
                    clonedAddonDetails[enteredAddonDetailsIndex].error = ""
                  } else {
                    isFormValidated = false
                    clonedAddonDetails[enteredAddonDetailsIndex].error =
                      "Add-on is compulsory for every participant. Please select at-least one add-on for every participant."
                  }
                }
              }
            } else {
              isFormValidated = false
              clonedAddonDetails = [
                ...clonedAddonDetails,
                {
                  addonId: addon.id,
                  error:
                    addon.merchandise.availability === "participant"
                      ? "Add-on is compulsory for every participant. Please select at-least one add-on for every participant."
                      : "Add-on is mandatory based on your selection!",
                  availability: addon.merchandise.availability,
                  requirement: addon.merchandise.requirements,
                  units: [],
                } as addonDetailsType,
              ]
            }
          }
        })
        setFieldValue("addonDetails", clonedAddonDetails)
        if (isFormValidated) {
          await handleNextStep?.(values)
          resetForm()
        }
      } catch (e: any) {
        Sentry.captureException(e)
        const errors = e?.response?.data?.errors || []
        for (const key of Object.keys(errors)) {
          setFieldError(key, errors[key][0])
        }
        resetForm()
      } finally {
        setFormSubmitting(false)
      }
    }

    const initialValues = useMemo(() => {
      return {
        addonDetails: formInitialValues?.addonDetails ?? [],
      }
    }, [])

    const checkAddOnCapacity = async ({
      key,
      unit,
      addOnindex,
      participantIndex,
      index,
      updatedValue,
      addon,
    }: any) => {
      // Below logic is for adding the old_quantity property when editing a cart item
      const addonCartItemDetails = formInitialValues?.addonDetails?.find(
        (addOn) => addOn.addonId === addon?.id
      )
      let addonPayload
      if (addonCartItemDetails?.availability === "customer") {
        const addonCartItemUnitDetails = addonCartItemDetails?.units?.find(
          (unitInfo) => unitInfo?.unitId === unit?.id
        )
        addonPayload = {
          cartItemId: addonCartItemUnitDetails?.id,
          old_quantity: addonCartItemUnitDetails?.unitCount,
        }
      } else if (addonCartItemDetails?.availability === "participant" && participantIndex) {
        const addonCartItemParticipantDetails = addonCartItemDetails?.units?.find(
          (addOn) => addOn?.participantId === participantIndex && addOn?.unitId === unit?.id
        )
        addonPayload = {
          cartItemId: addonCartItemParticipantDetails?.id,
          old_quantity: addonCartItemParticipantDetails?.unitCount,
        }
      }

      const payload = {
        quantities: [
          {
            unit_id: unit?.id,
            quantity: updatedValue,
            old_quantity: addonPayload?.old_quantity ?? 0,
            cartItemId: addonPayload?.cartItemId,
          },
        ],
      }
      try {
        const { data } = await ProductPricingServices.getMerchandisePricingByQuantities(
          addon?.id,
          payload,
          isIntegratedPage
        )

        const addonData = data?.map((item) => {
          return {
            ...item,
            product_name: addon?.name,
            product_id: addon?.id,
            participantIndex,
          }
        })

        function getUniqueListBy(newData) {
          const uniqueData: any = Array.from(
            new Map(
              newData.map((item) => [
                `${item.unit_id}_${item.product_id}_${item.participantIndex}`,
                item,
              ])
            ).values()
          )

          return [...uniqueData?.filter((item) => item.quantity > 0)]
        }
        setOrderSummery?.((prev) => {
          return {
            ...prev,
            addonProductData: getUniqueListBy([...(prev?.addonProductData || []), ...addonData]),
          }
        })

        setAddOnPrices((oldData) => {
          return {
            ...oldData,
            [`${addon?.id}-${unit?.id}-${participantIndex}`]: data[0]?.formatted_price ?? 0,
          }
        })
      } catch (e) {
        Sentry.captureException(e)
        console.log({ e })
      }
    }

    useEffect(() => {
      if (Object.keys(debounced).length !== 0) {
        checkAddOnCapacity(debounced)
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounced])

    useEffect(() => {
      if ((addOnList as unknown as ProductAddonType[])?.length) {
        setProductAddonsPriceDataPayload(() =>
          (addOnList as unknown as ProductAddonType[]).map((addon) => addon.id)
        )
      }
    }, [addOnList])

    return (
      <Formik
        initialValues={initialValues}
        innerRef={formikRef}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ values, setFieldValue }) => (
          <>
            {addOnList?.map((addon, addOnindex) => (
              <div
                key={addOnindex}
                className={
                  "w-full mb-4 pt-4 pb-5 gap-3.5 flex flex-col items-start bg-whiteBlack-100 rounded-md border border-neutral-450 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.06)]"
                }
              >
                {values.addonDetails.filter((ad) => ad.addonId === addon.id)?.[0]?.error && (
                  <p className={"paragraph14Medium text-red-600 mx-5 error-message"}>
                    {values.addonDetails.filter((ad) => ad.addonId === addon.id)[0].error}
                  </p>
                )}
                <div className={"flex flex-col gap-2 px-5"}>
                  <div className={"flex flex-row items-center justify-start gap-1"}>
                    <p className={"text16Semibold text-neutral-1000"}>{addon.name}</p>
                    {addon.merchandise.requirements === "required" ? (
                      <Badge color={"red"} className={"ml-2"} size={"xs"}>
                        Mandatory
                      </Badge>
                    ) : null}
                  </div>
                  {addon.description ? (
                    <DangerouslyHTMLTextPriview
                      content={addon?.description?.toString() ?? ""}
                      className={"paragraph14Regular text-neutral-600"}
                    />
                  ) : null}
                </div>
                {(addon.merchandise.availability === "participant"
                  ? participantsDetails?.participantsDetails
                  : Array(1).fill(0)
                )?.map((participant, participantIndex) => {
                  const uniqparticipantId =
                    addon.merchandise.availability === "participant"
                      ? (participant?.id ?? participantIndex)
                      : undefined
                  return (
                    <div
                      key={`${uniqparticipantId}-${participantIndex}`}
                      className={` w-full flex flex-col items-start justify-between px-5 ${addon.merchandise.availability === "participant" ? "border-t border-neutral-450 gap-3 pt-3.5" : ""} `}
                    >
                      {addon.merchandise.availability === "participant" &&
                      participant?.participantGroupName ? (
                        <div
                          className={
                            "flex items-center justify-center text16Semibold text-neutral-1000 gap-2"
                          }
                        >
                          <UserIcon className={"h-5 w-5 text-primary-600"} />
                          <span>{participant.participantGroupName}</span>
                        </div>
                      ) : null}
                      <div className={`flex flex-col items-start justify-start gap-3 w-full`}>
                        {productAddonsPriceData?.[addon.id]?.length &&
                          productAddonsPriceData?.[addon.id].map((unitInfo, index) => {
                            let targetAddonUnitIndex: number
                            const targetAddonIndex = values.addonDetails.findIndex((value) => {
                              let isTargetAddonIndexFound = false
                              if (value.addonId === addon.id) {
                                isTargetAddonIndexFound = true
                                value.units?.every((unit, index) => {
                                  if (
                                    addon.merchandise.availability === "participant" &&
                                    ((unit?.participantId &&
                                      participant?.id &&
                                      participant.id === unit.participantId) ||
                                      (unit?.participantGroupName ===
                                        participant?.participantGroupName &&
                                        unit?.participantUnitId ===
                                          participant?.participantUnitId)) &&
                                    unit?.unitId === unitInfo.unit.id
                                  ) {
                                    targetAddonUnitIndex = index
                                    return false
                                  } else if (
                                    addon.merchandise.availability === "customer" &&
                                    unit?.unitId === unitInfo.unit.id
                                  ) {
                                    targetAddonUnitIndex = index
                                    return false
                                  }
                                  return true
                                })
                              } else {
                                isTargetAddonIndexFound = false
                              }
                              return isTargetAddonIndexFound
                            })
                            const unitAddOnKey = `addonDetails.${targetAddonIndex}.units.${targetAddonUnitIndex}.unitCount`
                            const incrementDisable = values?.addonDetails?.[targetAddonIndex]
                              ?.units?.[targetAddonUnitIndex]?.unitCount
                              ? values.addonDetails[targetAddonIndex].units[targetAddonUnitIndex]
                                  .unitCount >= addon.merchandise.maximum_purchasable_quantity
                              : false
                            const decrementDisable = values?.addonDetails?.[targetAddonIndex]
                              ?.units?.[targetAddonUnitIndex]?.unitCount
                              ? values.addonDetails[targetAddonIndex].units[targetAddonUnitIndex]
                                  .unitCount === 0
                              : true
                            return (
                              <div key={index} className={"w-full"}>
                                {/* {addon.merchandise.availability === "customer" ? ( */}
                                <p className={"text14Medium text-neutral-900 mb-2"}>
                                  {unitInfo.unit.name}
                                  {unitInfo.unit?.description ? (
                                    <span className={"text14Regular text-neutral-900"}>
                                      {` (${unitInfo.unit.description})`}
                                    </span>
                                  ) : null}
                                </p>
                                {/* ) : null} */}

                                <div
                                  key={index}
                                  className={
                                    "flex flex-row items-center justify-between gap-2 w-full"
                                  }
                                >
                                  <div className={`flex items-center`}>
                                    <div className={"flex items-center gap-3"}>
                                      <button
                                        type={"button"}
                                        tabIndex={-1}
                                        onClick={() => {
                                          if (targetAddonIndex >= 0) {
                                            if (typeof targetAddonUnitIndex === "number") {
                                              const updatedValue =
                                                (values?.addonDetails?.[targetAddonIndex]?.units?.[
                                                  targetAddonUnitIndex
                                                ]?.unitCount ?? 1) - 1
                                              setFieldValue(unitAddOnKey, updatedValue)
                                              setDebounceParams({
                                                key: unitAddOnKey,
                                                unit: unitInfo?.unit,
                                                addOnindex: addon?.id,
                                                participantIndex: uniqparticipantId,
                                                index,
                                                updatedValue,
                                                addon,
                                              })
                                            }
                                          }
                                        }}
                                        disabled={decrementDisable}
                                      >
                                        <span
                                          className={`m-auto block h-[30px] w-[30px] rounded-md text-xl ${decrementDisable ? "text-primary-400 cursor-not-allowed bg-primary-200" : "text-primary-600 bg-primary-300"}`}
                                        >
                                          −
                                        </span>
                                      </button>
                                      <div
                                        className={
                                          "flex h-8 w-12 items-center justify-center border border-neutral-450 px-1 py-1 rounded-md"
                                        }
                                      >
                                        <div className={"text15Medium"}>
                                          {values?.addonDetails?.[targetAddonIndex]?.units?.[
                                            targetAddonUnitIndex
                                          ]?.unitCount ?? 0}
                                        </div>
                                      </div>
                                      <button
                                        type={"button"}
                                        tabIndex={+1}
                                        disabled={incrementDisable}
                                        onClick={() => {
                                          if (targetAddonIndex >= 0) {
                                            if (typeof targetAddonUnitIndex === "number") {
                                              const updatedValue =
                                                (values?.addonDetails?.[targetAddonIndex]?.units?.[
                                                  targetAddonUnitIndex
                                                ]?.unitCount ?? 1) + 1
                                              setFieldValue(unitAddOnKey, updatedValue)
                                              setDebounceParams({
                                                key: unitAddOnKey,
                                                unit: unitInfo?.unit,
                                                addOnindex: addon?.id,
                                                participantIndex: uniqparticipantId,
                                                index,
                                                updatedValue,
                                                addon,
                                              })
                                            } else {
                                              setFieldValue(
                                                `addonDetails.${targetAddonIndex}.units`,
                                                [
                                                  ...values?.addonDetails?.[targetAddonIndex].units,
                                                  {
                                                    unitCount: 1,
                                                    participantGroupName:
                                                      participant?.participantGroupName,
                                                    participantUnitId:
                                                      participant?.participantUnitId,
                                                    unitId: unitInfo.unit.id,
                                                    unitName: unitInfo.unit.name,
                                                    participantId: participant?.id,
                                                  },
                                                ]
                                              )
                                              setDebounceParams({
                                                key: unitAddOnKey,
                                                unit: unitInfo?.unit,
                                                addOnindex: addon?.id,
                                                participantIndex: uniqparticipantId,
                                                index,
                                                updatedValue: 1,
                                                addon,
                                              })
                                            }
                                          } else {
                                            setFieldValue("addonDetails", [
                                              ...values.addonDetails,
                                              {
                                                addonId: addon.id,
                                                error: "",
                                                availability: addon.merchandise.availability,
                                                requirement: addon.merchandise.requirements,
                                                units: [
                                                  {
                                                    unitCount: 1,
                                                    participantGroupName:
                                                      participant?.participantGroupName,
                                                    participantUnitId:
                                                      participant?.participantUnitId,
                                                    unitId: unitInfo.unit.id,
                                                    unitName: unitInfo.unit.name,
                                                    participantId: participant?.id,
                                                  },
                                                ],
                                              } as addonDetailsType,
                                            ])
                                            setDebounceParams({
                                              key: unitAddOnKey,
                                              unit: unitInfo?.unit,
                                              addOnindex: addon?.id,
                                              participantIndex: uniqparticipantId,
                                              index,
                                              updatedValue: 1,
                                              addon,
                                            })
                                          }
                                        }}
                                      >
                                        <span
                                          className={`m-auto block h-[30px] w-[30px] rounded-md text-xl ${incrementDisable ? "text-primary-400 cursor-not-allowed bg-primary-200" : "text-primary-600 bg-primary-300"}`}
                                        >
                                          +
                                        </span>
                                      </button>
                                    </div>
                                  </div>
                                  <div>
                                    <p className={"text14Semibold"}>
                                      {addonPrices?.[
                                        `${addon?.id}-${unitInfo?.unit?.id}-${uniqparticipantId}`
                                      ] ?? unitInfo?.formatted_price}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                      </div>
                    </div>
                  )
                })}
              </div>
            ))}
          </>
        )}
      </Formik>
    )
  }
)

export default AdditionalProductsList
